// Lib
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

// Components
import Icon from '../../../../../components/icons/Icon';
import SelectInput from '../../../../../components/select/SelectInput';

// Constants
import { REMINDER_VALUES } from '../../../../../../common/reminders/reminderConstants';
import { TIME_FORMAT_12H } from '../../../../../../common/users/timeFormatConstants';

// Selectors
import { userTimeFormatPreferenceSelector } from '../../../../../user/currentUserSelector';

// Styles
import './DueDateReminderSelect.scss';
import { useTranslator } from '../../../../../components/i18n/useTranslator';

type ReminderValue = keyof typeof REMINDER_VALUES | string;
type ReminderOption = { id: ReminderValue; label: string };

interface DueDateReminderSelectProps {
    value: string;
    onChange: (updateReminder: string) => void;
    hasDueDateTime: boolean;
    locale?: string;
}

/**
 * Formats a time for display in reminder options using the user's locale and time format preference
 * @param hour - The hour to format (0-23)
 * @param locale - The user's locale
 * @param timeFormat - The user's time format preference ('12h' or '24h')
 */
const getFormattedTime = (hour: number, locale: string, timeFormat: string): string => {
    const date = new Date();
    date.setHours(hour, 0, 0, 0);

    const use12Hour = timeFormat === TIME_FORMAT_12H;

    return new Intl.DateTimeFormat(locale, {
        hour: use12Hour ? 'numeric' : '2-digit',
        ...(use12Hour ? {} : { minute: '2-digit' }),
        hourCycle: use12Hour ? 'h12' : 'h23',
    }).format(date);
};

const DueDateReminderSelect = ({ value, onChange, hasDueDateTime, locale = 'en-US' }: DueDateReminderSelectProps) => {
    const translate = useTranslator('popup', 'assignment.dueDate.reminder');
    const timeFormat = useSelector(userTimeFormatPreferenceSelector);

    // Format the time once and reuse it
    const formattedTime = useMemo(() => getFormattedTime(9, locale, timeFormat), [locale, timeFormat]);

    const REMINDER_TRANSLATION_KEYS: Record<string, string> = {
        [REMINDER_VALUES.NONE]: 'none',
        [REMINDER_VALUES.WHEN_DUE]: 'whenDue',
        [REMINDER_VALUES.TEN_MINUTES_BEFORE]: 'tenMinutesBefore',
        [REMINDER_VALUES.ONE_HOUR_BEFORE]: 'oneHourBefore',
        [REMINDER_VALUES.NINE_AM_DAY_OF]: 'nineAmDayOf',
        [REMINDER_VALUES.NINE_AM_DAY_BEFORE]: 'nineAmDayBefore',
    };

    const getReminderOptions = (): ReminderOption[] => {
        const reminderIds = hasDueDateTime
            ? [
                  REMINDER_VALUES.NONE,
                  REMINDER_VALUES.WHEN_DUE,
                  REMINDER_VALUES.TEN_MINUTES_BEFORE,
                  REMINDER_VALUES.ONE_HOUR_BEFORE,
                  REMINDER_VALUES.NINE_AM_DAY_OF,
                  REMINDER_VALUES.NINE_AM_DAY_BEFORE,
              ]
            : [REMINDER_VALUES.NONE, REMINDER_VALUES.NINE_AM_DAY_OF, REMINDER_VALUES.NINE_AM_DAY_BEFORE];

        return reminderIds.map((reminderId) => {
            const translationKey = REMINDER_TRANSLATION_KEYS[reminderId];
            const isTimeBasedReminder =
                reminderId === REMINDER_VALUES.NINE_AM_DAY_OF || reminderId === REMINDER_VALUES.NINE_AM_DAY_BEFORE;

            return {
                id: reminderId,
                label: isTimeBasedReminder
                    ? translate(translationKey, { time: formattedTime })
                    : translate(translationKey),
            };
        });
    };

    const reminderOptions = getReminderOptions();

    const onSelectChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        onChange(event.target.value);
    };

    return (
        <div className="DueDateReminderSelect" style={{ cursor: 'pointer' }}>
            <div className="bell-icon-container">
                <Icon name="reminder-bell" />
            </div>
            <SelectInput className="select-input" value={value} onChange={onSelectChange}>
                {reminderOptions.map((reminderOption) => {
                    return (
                        <option key={reminderOption.id} value={reminderOption.id}>
                            {reminderOption.label}
                        </option>
                    );
                })}
            </SelectInput>
        </div>
    );
};

export default DueDateReminderSelect;
